import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, LockKeyhole } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/adminApi";
import { isEmail } from "@/lib/validate";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAdminAuth } from "@/components/admin/AdminAuth";
import { GlowField } from "@/components/site/GlowField";
import { Wordmark } from "@/components/site/Wordmark";
import { CTAButton } from "@/components/site/CTAButton";
import { ErrorAlert, Field, a11y, inputClass } from "@/components/forms/FormBits";

const META = { title: "Founders Sign In | ATA Forge", description: "Private sign-in for the ATA Forge founders." };

export default function AdminLogin() {
  usePageMeta(META);
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const found = {};
    if (!isEmail(values.email)) found.email = "Enter your email address.";
    if (!values.password) found.password = "Enter your password.";
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    setError("");
    try {
      await login(values.email.trim(), values.password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="relative isolate grid min-h-screen place-items-center overflow-hidden px-5 py-16">
      <GlowField />
      <div className="w-full max-w-md">
        <Wordmark testId="admin-login-wordmark" />
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0E1015]/85 p-6 shadow-[0_40px_100px_-40px_rgba(37,99,235,0.45)] backdrop-blur-xl sm:p-8">
          <span className="grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/[0.03]">
            <LockKeyhole className="h-5 w-5 text-[#2F6BFF]" aria-hidden="true" />
          </span>
          <h1 className="mt-6 text-2xl font-semibold tracking-[-0.03em]" data-testid="admin-login-title">
            Founders inbox
          </h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">Sign in to see contact messages and website check requests.</p>
          <form noValidate onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <Field id="admin-email" label="Email" error={errors.email}>
              <input
                {...a11y("admin-email", errors.email, true)}
                type="email"
                autoComplete="username"
                value={values.email}
                onChange={set("email")}
                className={`${inputClass} h-12`}
                data-testid="admin-email-input"
              />
            </Field>
            <Field id="admin-password" label="Password" error={errors.password}>
              <div className="relative">
                <input
                  {...a11y("admin-password", errors.password, true)}
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  value={values.password}
                  onChange={set("password")}
                  className={`${inputClass} h-12 pr-12`}
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-[#A1A1AA] transition-colors hover:text-white"
                  data-testid="admin-password-toggle"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
            {error && <ErrorAlert message={error} testId="admin-login-error" />}
            <CTAButton type="submit" size="lg" disabled={busy} arrow={!busy} className="w-full" testId="admin-login-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {busy ? "Signing in…" : "Sign in"}
            </CTAButton>
          </form>
        </div>
      </div>
    </main>
  );
}
